import { Button, ButtonMode, ButtonSize, Dropdown, Icon, ICONS } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import useSubmitGetHelp from '../../hooks/getHelp/useSubmitGetHelp';
import styles from './HelpForm.module.scss';

const cx = classNames.bind(styles);

interface HelpFormProps {
  onClose: Function;
}

interface GetHelpForm {
  inquiryType: string;
  subject: string;
  description: string;
}

const HelpForm = ({ onClose }: HelpFormProps): React.ReactElement => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<GetHelpForm>({ defaultValues: { inquiryType: 'feedback' } });
  const { mutate: submitGetHelp, isLoading: isSaving, isSuccess, reset } = useSubmitGetHelp();
  const [success, setSuccess] = useState(false);

  const inquiryOptions = [
    { key: 'Coverage Request', value: 'coverage', text: 'Coverage Request' },
    { key: 'General Feedback', value: 'feedback', text: 'General Feedback' },
    { key: 'Report an Issue', value: 'bug', text: 'Report an Issue' },
  ];

  useEffect(() => {
    if (isSuccess) {
      reset();
      showSuccess();
    }
  }, [isSuccess]);

  const showSuccess = () => {
    setSuccess(true);
    setTimeout(() => onClose(), 5000);
  };

  /* const resolvePageType = (pathname) => {
    try {
      const root = pathname
        .split('/')
        .filter((p) => p)[0]
        .toLowerCase();

      switch (root) {
        case 'feed':
          return 'Feeds';
        case 'articles':
          return 'Article';
        case 'company-list':
          return 'Companies Browse';
        case 'companies':
          return 'Company Page';
        case 'user':
          return 'Profile and Settings';
        case 'deals-pipeline':
          return 'Deals Pipeline';
        case 'special-situations':
          return 'Special Situations';
        case 'market-stats':
          return 'Market Stats';
        case 'data-tools':
          return 'Data Tools';
        default:
          return 'Other';
      }
    } catch (e) {
      return 'Other';
    }
  }; */

  const onSubmit = (values) => {
    const { innerWidth, innerHeight, location } = window;

    submitGetHelp({
      pageUrl: location.href,
      summary: values.subject,
      content: values.description,
      userPlatform: 'web',
      userAgent: navigator.userAgent,
      windowSize: `${innerHeight}x${innerWidth}`,
    });
  };

  return (
    <div className={cx('body')} data-testid="get-help-form">
      <form className="ui form" onSubmit={handleSubmit(onSubmit)}>
        <div className={cx('header')}>
          <div className={cx('text')}>How Can We Help?</div>
          <div className={cx('actions')}>
            <div className={cx('action')} onClick={() => onClose()} data-testid="get-help-form-close">
              <Icon name={ICONS.Close} height={18} width={18} />
            </div>
          </div>
        </div>
        {success && (
          <div className={cx('success')}>
            <div className={cx('thankyou')}>
              Thank you for submitting
              <br /> your feedback.
            </div>
          </div>
        )}
        {!success && (
          <>
            <div className={cx('form-body')}>
              <div className="field">
                <label htmlFor="inquiryType">Inquiry type</label>
                <Controller
                  name="inquiryType"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { value, onChange } }) => (
                    <Dropdown
                      className={cx('inquiry-options', { required: true })}
                      placeholder="Select your inquiry type"
                      fluid
                      value={value}
                      options={inquiryOptions}
                      onOptionSelect={(v) => onChange(v)}
                    />
                  )}
                />
              </div>
              <div className="field">
                <label htmlFor="subject">Subject</label>
                <input
                  type="text"
                  className={cx({ error: errors.subject })}
                  {...register('subject', { required: { value: true, message: 'Subject is required' } })}
                  placeholder="Enter the subject of your feedback"
                />
                {errors.subject && <div className={cx('error-message')}>{errors.subject.message}</div>}
              </div>
              <div className="field">
                <label htmlFor="description">Description</label>
                <textarea
                  className={cx({ error: errors.description })}
                  {...register('description', { required: { value: true, message: 'Description is required' } })}
                  placeholder="Describe your feedback"
                  rows={15}
                />
                {errors.description && <div className={cx('error-message')}>{errors.description.message}</div>}
              </div>
            </div>
            <div className={cx('buttons')}>
              <Button
                mode={ButtonMode.SECONDARY}
                size={ButtonSize.MEDIUM}
                onClick={() => onClose()}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button mode={ButtonMode.PRIMARY} size={ButtonSize.MEDIUM} type="submit" loading={isSaving}>
                Submit
              </Button>
            </div>
          </>
        )}
      </form>
    </div>
  );
};

export default HelpForm;
